import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { findByB2BCustomQuotation, downloadB2BQuotation } from "./services/b2bQuotation.services";
import { LoadingPanel } from "../../components/layout/Loading";
import B2BQuotationDocument from "./components/B2BQuotationDocument";
import "./b2bView.scss";
import { FaDownload, FaPrint, FaShareAlt } from "react-icons/fa";
import IconComponent from "../../components/common/IconComponent";
import ShareB2BQuotationDialog from "./components/ShareB2BQuotationDialog";
import { openDialog } from "../../components/dialog/dialogSlice";

/**
 * B2BQuotationPrint is a layout-less page used for a separate view or printing.
 * It strictly renders the B2BQuotationDocument.
 */
const B2BQuotationPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setLoading(true);
        const res = await dispatch(findByB2BCustomQuotation(Number(id)));
        if (res.meta.requestStatus === "fulfilled") {
          setData(res.payload);
        }
        setLoading(false);
      }
    };
    fetchData();
  }, [id, dispatch]);

  const handleDownload = async () => {
    if (id) {
      setDownloading(true);
      try {
        const res = await dispatch(downloadB2BQuotation(Number(id)));
        if (downloadB2BQuotation.fulfilled.match(res)) {
          const blob = res.payload as Blob;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Quotation-${data?.QuotationReference || data?.ID || id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Download error:", error);
      } finally {
        setDownloading(false);
      }
    }
  };

  const handleOpenShare = () => {
    dispatch(openDialog("shareB2BQuotation"));
  };

  if (loading) return <LoadingPanel />;
  if (!data) return <div className="p-5 text-center">Quotation not found.</div>;

  return (
    <div className="b2b-print-page bg-white">
      <ShareB2BQuotationDialog data={data} />
      {/* ── Action Buttons (Hidden in Print) ────────────────────────────────── */}
      <div className="no-print p-4 text-center border-bottom mb-4 bg-light sticky-top">
        <button
          className="btn btn-primary px-4 fw-bold me-2"
          onClick={() => window.print()}
        >
          <IconComponent icon={FaPrint} className="me-2" /> Print
        </button>
        <button
          className="btn btn-success px-4 fw-bold me-2 text-white"
          onClick={handleDownload}
          disabled={downloading}
          style={{ minWidth: "160px" }}
        >
          {downloading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Downloading...
            </>
          ) : (
            <>
              <IconComponent icon={FaDownload} className="me-2" /> Download PDF
            </>
          )}
        </button>
        <button
          className="btn btn-info px-4 fw-bold text-white"
          onClick={handleOpenShare}
        >
          <IconComponent icon={FaShareAlt} className="me-2" /> Share
        </button>
      </div>

      {/* ── Quotation Document ─────────────────────────────────────────────── */}
      <div className="print-content-wrapper">
        <B2BQuotationDocument data={data} />
      </div>
    </div>
  );
};

export default B2BQuotationPrint;
